<template>
  <div class="grid-map-menu-body">
    <search-form
      class="grid-map-search-form"
      :placeholder="placeholder"
      :keyword="keyword"
      :handleInput="searchOwnVessel"
    />

    <component
      :is="scrollbarTag"
      class="grid-map-scroll-area--customizer"
      :settings="settings"
      :key="$vs.rtl"
    >
      <ul class="mt-1">
        <li
          class="aisea-pb-6 aisea-pl-4 aisea-pr-4"
          v-for="ownVessel in resultOwnVessels"
          :key="ownVessel.id"
        >
          <vessel-card
            :vessel="ownVessel"
            :jumpToVessel="jumpToVessel"
            :isSelected="trackingTarget === ownVessel.id"
            :vesselPosition="resultVesselPosition(ownVessel.id)"
          ></vessel-card>
        </li>
      </ul>
    </component>
  </div>
</template>

<script>
/**
 * 【右メニュー】自社船メニューを描画するためのコンポーネント
 * @parent O.RightMenuBase
 */
import { DateUtil } from "@/utils";
import moment from "moment-timezone";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import VesselCard from "@/components/molecules/VesselCard";
import SearchForm from "@/components/molecules/SearchForm";
import { createNamespacedHelpers } from "vuex";
import FireStoreService from "@/services/fireStoreService";

const { mapState: mapStateOfLiveMap } = createNamespacedHelpers("liveMap");

const {
  mapActions: mapActionsOfGridMap,
  mapState: mapStateOfGridMap,
  mapMutations: mapMutationsOfGridMap,
  mapGetters: mapGettersOfGridMap,
} = createNamespacedHelpers("gridMap");

export default {
  name: "O.OwnVessel",
  components: {
    VuePerfectScrollbar,
    VesselCard,
    SearchForm,
  },
  props: {
    /** スクロールの設定 */
    settings: {
      type: Object,
      required: true,
    },
    vesselPositions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      // 検索機能で使用
      placeholder: "キーワードを入力",
      searchedOwnVessel: [],
      keyword: "",
    };
  },
  computed: {
    ...mapStateOfLiveMap({
      ownVessels: (state) => state.ownVessels,
    }),
    ...mapStateOfGridMap({
      activeSidebarForGrid: (state) => state.activeSidebarForGrid,
    }),
    ...mapGettersOfGridMap(["getTrackingTarget"]),
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    resultOwnVessels() {
      return this.keyword.length ? this.searchedOwnVessel : this.ownVessels;
    },
    // FireStoreで追跡設定しているターゲットがvesselならIDを返す
    trackingTarget() {
      return this.getTrackingTarget({
        mapId: this.activeSidebarForGrid.mapId,
        type: "vessel",
      });
    },
  },
  methods: {
    ...mapActionsOfGridMap(["getLastPositionWithTimeout"]),
    ...mapMutationsOfGridMap([
      "UPDATE_GRID_MAP_SETTINGS",
      "UPDATE_LAST_TRACKING_POSITION",
      "SET_EXPIRED_POPUP_FOR_GRID",
    ]),
    // IDが一致する船のポジションを返す
    resultVesselPosition(vesselId) {
      const vessel = this.vesselPositions.find((vesselPosition) => {
        return vesselPosition.vesselId === vesselId;
      });
      if (vessel) {
        return vessel.vesselPosition;
      }
      return null;
    },
    // クリックしたカードの船に移動
    async jumpToVessel(vessel) {
      if (!vessel) {
        this.showFailureMessage();
        return;
      }
      const mapId = this.activeSidebarForGrid.mapId;
      if (!mapId) return;
      // 選択中のカードをクリックしたら選択を解除しデフォルトポジションに戻る
      if (this.trackingTarget === vessel.id) {
        this.UPDATE_LAST_TRACKING_POSITION({
          mapId: mapId,
          center: [],
        });
        return;
      }
      this.$vs.loading({ container: `#${mapId}.vs-con-loading__container` });
      const lastPosition = await this.getLastPositionWithTimeout(vessel.id);
      this.$vs.loading.close(
        `#${mapId}.vs-con-loading__container > .con-vs-loading`,
      );
      if (
        !lastPosition ||
        lastPosition.longitude == null ||
        lastPosition.latitude == null
      ) {
        this.showFailureMessage();
        return;
      }
      let center = [lastPosition.longitude, lastPosition.latitude];
      const trackingTarget = { type: "vessel", id: vessel.id };
      // store(vuex)更新
      this.UPDATE_LAST_TRACKING_POSITION({
        mapId: mapId,
        center: center,
      });
      this.UPDATE_GRID_MAP_SETTINGS({
        map_id: mapId,
        tracking_target: trackingTarget,
      });
      // firestore更新
      await FireStoreService.setGridMapSettings(mapId, {
        tracking_target: trackingTarget,
      });
      this.showNotifyTimeout(lastPosition);
    },
    // 最終計測時間が10分前より遅い場合はポップアップメッセージを表示する
    showNotifyTimeout(lastPosition) {
      const tenMinutesAgo = moment().subtract(10, "minutes").utc().unix();
      const lastPosAt = moment(lastPosition.last_position_at).unix();
      if (lastPosAt < tenMinutesAgo) {
        const name = lastPosition.vessel_name_ja || lastPosition.vessel_name;
        const dateFormat = moment(lastPosition.last_position_at).format(
          "YYYY/MM/DD HH:mm",
        );
        let elapsed = DateUtil.elapsedTime(dateFormat);
        const popupObject = {
          closeOnClick: false,
          closeButton: true,
          offset: 15,
          className: "expired-vessel-live-position-popup",
        };
        const center = [lastPosition.longitude, lastPosition.latitude];
        const text = `${name}はこの位置で、${elapsed}(${dateFormat})に記録されました。`;
        this.createExpiredPopup(popupObject, center, text);
      }
    },
    // 座標取得に失敗したときのメッセージ
    showFailureMessage() {
      this.$vs.notify({
        text: "位置情報が取得できません。",
        color: "danger",
        iconPack: "feather",
        icon: "icon-alert-circle",
        position: "top-center",
      });
    },
    // グリッドマップにポップアップメッセージを表示
    createExpiredPopup(popupObject, center, text) {
      this.SET_EXPIRED_POPUP_FOR_GRID({
        mapId: this.activeSidebarForGrid.mapId,
        popupObject: popupObject,
        center: center,
        text: text,
      });
    },
    // 自社船のインクリメントサーチ
    searchOwnVessel(nextKeyword) {
      this.keyword = nextKeyword;
      if (!this.keyword.length) {
        this.searchedOwnVessel = this.ownVessels;
        return;
      }
      const filteredOwnVessel = this.ownVessels.filter((vessel) => {
        const searchTargetArray = [vessel.name_ja, vessel.name];
        return searchTargetArray.some((item) => {
          if (!item) {
            return false;
          }
          return (
            item.toUpperCase().indexOf(this.keyword.trim().toUpperCase()) !== -1
          );
        });
      });
      this.searchedOwnVessel = filteredOwnVessel;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";
</style>
